import { useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { APP_CONFIG } from '@/config/app.config';
import { BRAND_COLOR, LOGO, SHARE_IMAGE } from '@/shared/brand';
import { BANSHEE_PATH } from '@/shared/pricing';

/**
 * Search and share tags for a single-page app.
 * index.html is platform-owned, so everything here is written into <head> at runtime:
 * RootLayout applies route defaults (title, canonical, robots) on every navigation, and
 * public pages refine them with useSeo / useJsonLd.
 */

export const SITE_NAME = APP_CONFIG.name;

export const DEFAULT_TITLE = `${SITE_NAME} · IPTV with original live channels and an on-demand vault`;

export const DEFAULT_DESCRIPTION =
  'Ghost Cable streams original live channels and an on-demand vault to every screen you own. No cable box, no contracts. Plans from $4 with 1 to 5 connections.';

/** Back office, account, checkout and sign-in screens stay out of search results. */
const PRIVATE_PATH =
  /^\/(admin|account|checkout|access-denied)(\/|$)|^\/([\w-]+\/)?(login|signup|callback|forgot-password|reset-password|verify-email|accept-invite)(\/|$)/;

const ROUTE_TITLES = [
  [/^\/admin(\/|$)/, 'Console'],
  [/^\/account(\/|$)/, 'My account'],
  [/^\/checkout(\/|$)/, 'Checkout'],
  [/^\/([\w-]+\/)?login(\/|$)/, 'Sign in'],
  [/^\/([\w-]+\/)?signup(\/|$)/, 'Create your account'],
  [/^\/([\w-]+\/)?(forgot-password|reset-password)(\/|$)/, 'Reset your password'],
  [/^\/access-denied(\/|$)/, 'Access denied'],
];

export function isPrivatePath(pathname) {
  return PRIVATE_PATH.test(pathname) || pathname.startsWith(BANSHEE_PATH);
}

// Set VITE_SITE_URL (e.g. https://ghostcable.tv) once the custom domain is live so canonical
// links and share cards always point there, even when the site is opened on the preview address.
const SITE_URL = String(import.meta.env.VITE_SITE_URL ?? '')
  .trim()
  .replace(/\/+$/, '');

function siteOrigin() {
  if (/^https?:\/\/[^/]+$/i.test(SITE_URL)) return SITE_URL;
  return typeof window === 'undefined' ? '' : window.location.origin;
}

export function absoluteUrl(path = '/') {
  if (/^https?:\/\//.test(path)) return path;
  return `${siteOrigin()}${path.startsWith('/') ? path : `/${path}`}`;
}

function fullTitle(title) {
  if (!title) return DEFAULT_TITLE;
  return title.includes(SITE_NAME) ? title : `${title} · ${SITE_NAME}`;
}

/** Trim to a search-snippet length on a word boundary. */
export function snippet(text, max = 160) {
  const clean = String(text ?? '')
    .replace(/\s+/g, ' ')
    .trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1).replace(/\s+\S*$/, '')}…`;
}

/* ---------- head tag helpers ---------- */

function upsert(selector, create) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    el.setAttribute('data-seo', '');
    document.head.appendChild(el);
  }
  return el;
}

function setMeta(attr, key, content) {
  const selector = `meta[${attr}="${key}"]`;
  if (content == null || content === '') {
    document.head.querySelector(selector)?.remove();
    return;
  }
  const el = upsert(selector, () => {
    const m = document.createElement('meta');
    m.setAttribute(attr, key);
    return m;
  });
  el.setAttribute('content', String(content));
}

function setLink(rel, href, attrs = {}) {
  const extra = attrs.sizes ? `[sizes="${attrs.sizes}"]` : '';
  const el = upsert(`link[rel="${rel}"]${extra}`, () => {
    const l = document.createElement('link');
    l.setAttribute('rel', rel);
    return l;
  });
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  el.setAttribute('href', href);
}

function setJsonLd(key, data) {
  const selector = `script[type="application/ld+json"][data-seo-key="${key}"]`;
  if (!data) {
    document.head.querySelector(selector)?.remove();
    return;
  }
  const el = upsert(selector, () => {
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.setAttribute('data-seo-key', key);
    return s;
  });
  el.textContent = JSON.stringify(data);
}

/* ---------- site-wide tags, written once ---------- */

const ORG_ID = () => `${absoluteUrl('/')}#organization`;

function siteGraph() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': ORG_ID(),
        name: SITE_NAME,
        url: absoluteUrl('/'),
        slogan: APP_CONFIG.tagline,
        logo: { '@type': 'ImageObject', url: LOGO.icon512, width: 512, height: 512 },
        image: SHARE_IMAGE.url,
        areaServed: 'Worldwide',
      },
      {
        '@type': 'WebSite',
        '@id': `${absoluteUrl('/')}#website`,
        name: SITE_NAME,
        url: absoluteUrl('/'),
        description: DEFAULT_DESCRIPTION,
        inLanguage: 'en',
        publisher: { '@id': ORG_ID() },
      },
    ],
  };
}

// Font files are fetched in CORS mode, so that connection needs crossorigin; CSS and images do not.
const PRECONNECT = [
  ['https://fonts.googleapis.com', false],
  ['https://fonts.gstatic.com', true],
  ['https://storage.googleapis.com', false],
];

function preconnect([href, cors]) {
  if (document.head.querySelector(`link[rel="preconnect"][href="${href}"]`)) return;
  const l = document.createElement('link');
  l.rel = 'preconnect';
  l.href = href;
  if (cors) l.crossOrigin = 'anonymous';
  document.head.appendChild(l);
}

function installSiteHead() {
  if (window.__gcSeoReady) return;
  window.__gcSeoReady = true;

  PRECONNECT.forEach(preconnect);

  // The platform's index.html links /favicon.ico; point it (and every other icon slot) at the logo.
  document.head.querySelectorAll('link[rel="icon"]:not([data-seo])').forEach((l) => l.remove());
  setLink('icon', LOGO.favicon, { type: 'image/png', sizes: '32x32' });
  setLink('icon', LOGO.icon192, { type: 'image/png', sizes: '192x192' });
  setLink('icon', '/favicon.svg', { type: 'image/svg+xml', sizes: 'any' });
  setLink('apple-touch-icon', LOGO.apple, { sizes: '180x180' });

  setMeta('name', 'theme-color', BRAND_COLOR);
  setMeta('name', 'application-name', SITE_NAME);
  setMeta('name', 'format-detection', 'telephone=no');
  setMeta('property', 'og:site_name', SITE_NAME);
  setMeta('property', 'og:locale', 'en_US');
  setMeta('name', 'twitter:card', 'summary_large_image');

  setJsonLd('site', siteGraph());
}

/* ---------- per-page tags ---------- */

/**
 * Write the page's title, description, canonical, robots, Open Graph and Twitter tags.
 * `path` defaults to the current pathname (query and hash are never part of the canonical).
 */
export function applySeo({
  title,
  description = DEFAULT_DESCRIPTION,
  path,
  image = SHARE_IMAGE,
  type = 'website',
  noindex,
} = {}) {
  if (typeof document === 'undefined') return;
  installSiteHead();

  const pathname = path ?? window.location.pathname;
  const hidden = noindex ?? isPrivatePath(pathname);
  const url = absoluteUrl(pathname);
  const heading = fullTitle(title);
  const summary = snippet(description);
  const img = typeof image === 'string' ? { url: image } : image;

  document.title = heading;
  setMeta('name', 'description', summary);
  setMeta('name', 'robots', hidden ? 'noindex, nofollow' : 'index, follow, max-image-preview:large');
  setLink('canonical', url);

  setMeta('property', 'og:type', type);
  setMeta('property', 'og:title', heading);
  setMeta('property', 'og:description', summary);
  setMeta('property', 'og:url', url);
  setMeta('property', 'og:image', img?.url);
  setMeta('property', 'og:image:width', img?.width);
  setMeta('property', 'og:image:height', img?.height);
  setMeta('property', 'og:image:alt', img?.alt ?? heading);

  setMeta('name', 'twitter:title', heading);
  setMeta('name', 'twitter:description', summary);
  setMeta('name', 'twitter:image', img?.url);
  setMeta('name', 'twitter:image:alt', img?.alt ?? heading);
}

function routeTitle(pathname) {
  return ROUTE_TITLES.find(([re]) => re.test(pathname))?.[1];
}

/**
 * Route defaults, used once in RootLayout. A layout effect, so it lands before the
 * page's own useSeo (a passive effect) and never overwrites it.
 */
export function useRouteSeo() {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    applySeo({ title: routeTitle(pathname), path: pathname });
  }, [pathname]);
}

/** Page-level tags. Pass `null` while the page is still loading what it describes. */
export function useSeo(options) {
  const key = options ? JSON.stringify(options) : '';

  useEffect(() => {
    if (options) applySeo(options);
    // `key` captures every option by value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

/** Structured data for the current page; removed again when the component unmounts. */
export function useJsonLd(key, data) {
  const empty = !data || (Array.isArray(data) && data.length === 0);
  const json = empty ? '' : JSON.stringify(data);

  useEffect(() => {
    if (!json) return undefined;
    setJsonLd(key, JSON.parse(json));
    return () => setJsonLd(key, null);
  }, [key, json]);
}

/* ---------- schema.org builders ---------- */

export function faqSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

/** A product with one price, or a low–high range across its terms (all USD). */
export function productSchema({ name, description, path, image, prices }) {
  const list = (prices ?? []).map(Number).filter((n) => n > 0);
  const url = absoluteUrl(path);
  const seller = { '@id': ORG_ID() };
  let offers;
  if (list.length === 1) {
    offers = {
      '@type': 'Offer',
      price: list[0],
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url,
      seller,
    };
  } else if (list.length > 1) {
    offers = {
      '@type': 'AggregateOffer',
      lowPrice: Math.min(...list),
      highPrice: Math.max(...list),
      offerCount: list.length,
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url,
      seller,
    };
  }
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description: snippet(description, 300),
    url,
    image: image || LOGO.icon512,
    brand: { '@type': 'Brand', name: SITE_NAME },
    ...(offers ? { offers } : {}),
  };
}

export function breadcrumbSchema(crumbs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: absoluteUrl(c.path),
    })),
  };
}
